import { useEffect, useState } from 'react'
import { Col, Container, Row, Table, Card } from 'react-bootstrap'
import { useLocation, useParams } from 'react-router-dom'
import Breadcrumb from '../../../components/Label/Breadcrumb'
import api from '../../../config/AxiosInstance'
import { convertDateWithoutTime } from '../../../helpers/constants'
import * as Common from '../../../utils/Common'
import Loading from '../../../components/atoms/Loading'
import { Building2, Warehouse, Calendar } from 'lucide-react'

const labelDetailStyle = {
  fontSize: '0.75rem',
  fontWeight: '700',
  color: '#333333',
  textTransform: 'uppercase',
  marginBottom: '0.25rem',
  letterSpacing: '0.5px'
}

const valueDetailStyle = {
  fontSize: '1rem',
  fontWeight: '600',
  color: '#333333',
  marginBottom: '0'
}

const tableHeaderStyle = {
  fontSize: '14px',
  fontWeight: '600',
  color: '#333333',
  letterSpacing: '0.5px',
  backgroundColor: '#f8f9fa',
  borderBottom: '2px solid #e9ecef'
}

const cardStyle = {
  border: 'none',
  boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
  borderRadius: '12px',
  padding: '1.5rem',
  marginBottom: '1.5rem',
  backgroundColor: '#fff'
}

const PurchaseOrderDetail = () => {
  const [detail, setDetail] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const { code } = useParams()
  const { state } = useLocation()

  useEffect(() => {
    loadData(code)
  }, [code])

  const loadData = async (code) => {
    try {
      setIsLoading(true)
      const res = await api.get(`inbound/purchase-order/show?po=${code}`)
      const { data } = res.data
      setDetail(data)
    } catch (err) {
      const errMsg = err?.response?.data?.errors?.message || 'An error occurred'
      Common.showPopUpErrorMessage(errMsg)
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return <Loading />
  }

  return (
    <>
      <Container fluid className="px-4 mb-5">
        <Row>
          <Col md={12} className="mt-3 mb-4">
            <Breadcrumb
              label={detail.po_name}
              link="/purchase-order"
              back="PURCHASE ORDER"
              status={detail.status}
              type="ReceiveBill"
              stateValue={state}
            />
          </Col>
        </Row>

        <Card style={cardStyle} className="mb-4">
          <Row className="gy-4">
            <Col xs={12} md={4} lg={4}>
              <div className="d-flex align-items-center">
                <div
                  className="rounded bg-light d-flex align-items-center justify-content-center me-3"
                  style={{ width: '48px', height: '48px' }}
                >
                  <Building2 size={24} className="text-secondary" />
                </div>
                <div>
                  <div style={labelDetailStyle}>PRINCIPLE</div>
                  <div style={valueDetailStyle}>{detail?.principle || '-'}</div>
                </div>
              </div>
            </Col>
            <Col xs={12} md={4} lg={4}>
              <div className="d-flex align-items-center">
                <div
                  className="rounded bg-light d-flex align-items-center justify-content-center me-3"
                  style={{ width: '48px', height: '48px' }}
                >
                  <Warehouse size={24} className="text-secondary" />
                </div>
                <div>
                  <div style={labelDetailStyle}>DESTINATION WAREHOUSE</div>
                  <div style={valueDetailStyle}>
                    {detail?.dest_warehouse || '-'}
                  </div>
                </div>
              </div>
            </Col>
            <Col xs={12} md={4} lg={4}>
              <div className="d-flex align-items-center">
                <div
                  className="rounded bg-light d-flex align-items-center justify-content-center me-3"
                  style={{ width: '48px', height: '48px' }}
                >
                  <Calendar size={24} className="text-secondary" />
                </div>
                <div>
                  <div style={labelDetailStyle}>PO DATE</div>
                  <div style={valueDetailStyle}>
                    {detail?.po_date
                      ? convertDateWithoutTime(detail?.po_date)
                      : '-'}
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Card>

        <Row className="mb-4">
          <Col md={12}>
            <h5
              className="mb-3 text-secondary fw-bold"
              style={{ fontSize: '0.9rem', letterSpacing: '0.5px' }}
            >
              Items ({detail?.items?.length || 0})
            </h5>
          </Col>
          <Col md={12}>
            <Table
              hover
              responsive
              className="shadow-sm bg-white"
              style={{
                fontSize: '0.9rem',
                borderRadius: '12px',
                overflow: 'hidden'
              }}
            >
              <thead style={{ backgroundColor: '#f8f9fa' }}>
                <tr>
                  <th
                    className="p-2 align-middle"
                    style={{ ...tableHeaderStyle, width: '60px' }}
                  >
                    #
                  </th>
                  <th className="p-2 align-middle" style={tableHeaderStyle}>
                    Item Code - Name
                  </th>
                  <th
                    className="p-2 text-end align-middle"
                    style={tableHeaderStyle}
                  >
                    PO Qty
                  </th>
                </tr>
              </thead>
              <tbody>
                {detail?.items?.map((item, index) => (
                  <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td className="p-2 align-middle text-center text-muted">
                      {index + 1}
                    </td>
                    <td className="p-2 align-middle fw-semibold text-primary">
                      {item.code} - {item.name}
                    </td>
                    <td className="p-2 align-middle text-end text-dark">
                      {item.qty}
                    </td>
                  </tr>
                ))}
                {!detail?.items?.length && (
                  <tr>
                    <td colSpan="3" className="text-center p-4 text-muted">
                      No items found
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </Col>
        </Row>

        {detail?.prp_items && detail.prp_items.length > 0 && (
          <Row>
            <Col md={12}>
              <h5
                className="mb-3 text-secondary fw-bold"
                style={{ fontSize: '0.9rem', letterSpacing: '0.5px' }}
              >
                PRP Items ({detail?.prp_items?.length || 0})
              </h5>
            </Col>
            <Col md={12}>
              <Table
                hover
                responsive
                className="shadow-sm bg-white"
                style={{
                  fontSize: '0.9rem',
                  borderRadius: '12px',
                  overflow: 'hidden'
                }}
              >
                <thead style={{ backgroundColor: '#f8f9fa' }}>
                  <tr>
                    <th
                      className="p-2 align-middle"
                      style={{ ...tableHeaderStyle, width: '60px' }}
                    >
                      #
                    </th>
                    <th className="p-2 align-middle" style={tableHeaderStyle}>
                      Item Code - Name
                    </th>
                    <th
                      className="p-2 text-end align-middle"
                      style={tableHeaderStyle}
                    >
                      PO Qty
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {detail?.prp_items?.map((item, index) => (
                    <tr
                      key={index}
                      style={{ borderBottom: '1px solid #f0f0f0' }}
                    >
                      <td className="p-2 align-middle text-center text-muted">
                        {index + 1}
                      </td>
                      <td className="p-2 align-middle fw-semibold text-primary">
                        {item?.code} - {item?.name}
                      </td>
                      <td className="p-2 text-end align-middle text-dark">
                        {item?.qty}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Col>
          </Row>
        )}
      </Container>
    </>
  )
}

export default PurchaseOrderDetail
